import React, { isValidElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, Select, TreeDataNode, TreeSelect } from 'antd';
import { get, replace } from 'lodash-es';

import { DebounceSelect } from 'components/common';
import { classnames } from 'lib/classnames';
import { AUTO_USER_FIELD_TYPES, FIELD_TYPE_KEY_MAPPINGS } from 'lib/global';
import { useConstantConfig } from 'lib/hooks';
import useI18n from 'lib/hooks/useI18n';
import { SelectValues, UserFieldsInter } from 'lib/types/automation';
import { getUserOptionsByName, getUserOptionsByValues } from 'lib/users';
import { getGroupsTree, getUserCustomFieldOptionsByName, getUserCustomFieldOptionsByValues } from 'lib/useUser';
import { getWorkspaceRoleOptionsByName, getWorkspaceRoleOptionsByValues } from 'lib/workspaceRole';

import styles from './index.module.less';

const cx = classnames(styles);
interface UserTypeFieldsInter {
  value?: UserFieldsInter;
  onChange?: (e: any, type?: string, item?: any) => void; // item和e可能是任意类型
  name?: string;
  type?: string;
  enabled?: boolean;
  fetchValues?: (values: string[] | SelectValues[]) => Promise<SelectValues[]>;
  fetchOptions?: (search: string) => Promise<SelectValues[]>;
  groupList?: TreeDataNode[];
  fieldType?: string;
  readonly?: boolean;
  hiddenCustomUserField?: boolean;
  hiddenWorkflowField?: boolean;
  hiddenCommentField?: boolean;
  hiddenWorkspaceRoleField?: boolean;
  hiddenCustomUserFieldSource?: boolean; // 隐藏用户字段复制来源
  children?: React.ReactNode;
}

const SYSTEM_USER = [FIELD_TYPE_KEY_MAPPINGS.CreatedBy, FIELD_TYPE_KEY_MAPPINGS.UpdatedBy];

const UserField: React.FC<UserTypeFieldsInter> = props => {
  const { name, fetchValues, fetchOptions, children, type, onChange, value, groupList = [], readonly } = props;
  const [checked, onChecked] = useState(!!value[type]?.enabled);
  const { t } = useI18n();

  const onCheck = useCallback(
    e => {
      const checked = e?.target?.checked;
      onChecked(checked);
      onChange && onChange(checked, type);
    },
    [onChange, type],
  );

  const onFieldChange = useCallback(
    (keys, items) => {
      onChange && onChange(keys, type, items);
    },
    [onChange, type],
  );

  const placeholder = useMemo(() => {
    if (type === 'group') {
      return t('placeholder.users.userGroup');
    } else if (type === 'field') {
      return t('placeholder.default.selectUserField');
    } else if (type === 'user') {
      return t('placeholder.default.selectUser');
    }
    return t('placeholder.default.select');
  }, [type, t]);

  return (
    <div className="user-fields">
      <Checkbox
        className={cx('user-field-check')}
        onChange={onCheck}
        checked={value[type]?.enabled}
        disabled={readonly}
      >
        {name}
      </Checkbox>
      {type === 'group' ? (
        <TreeSelect
          className={cx('user-field-input')}
          dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          showSearch
          disabled={!value[type]?.enabled || readonly}
          filterTreeNode={(value, option) => option.props?.title?.toLowerCase().indexOf(value?.toLowerCase()) > -1}
          placeholder={placeholder}
          treeDataSimpleMode
          value={value[type]?.value}
          onChange={onFieldChange}
          multiple
          treeDefaultExpandAll
          treeData={groupList}
        />
      ) : (
        <DebounceSelect
          className={cx('user-field-input')}
          mode="multiple"
          disabled={!value[type]?.enabled || readonly}
          value={value[type]?.value}
          allowClear
          showSearch
          fetchOptions={fetchOptions}
          fetchValues={fetchValues}
          placeholder={placeholder}
          onChange={onFieldChange}
        />
      )}
      {isValidElement(children)
        ? React.cloneElement(children, { ...props, disabled: !checked, type: 'fieldType', fieldType: type } as unknown)
        : children}
    </div>
  );
};

const SourceSelect: React.FC<UserTypeFieldsInter> = props => {
  const { onChange, type, value, fieldType, readonly } = props;
  const { t } = useI18n();
  const { CLONE_TYPE_LIST } = useConstantConfig();

  const onFieldChange = useCallback(
    (keys, items) => {
      onChange && onChange(keys, type, items);
    },
    [onChange, type],
  );

  return (
    <>
      <Select
        placeholder={t('placeholder.default.copy')}
        className={cx('user-field-group-extra')}
        {...props}
        onChange={onFieldChange}
        value={value?.[fieldType]?.source?.type}
        disabled={readonly}
      >
        {CLONE_TYPE_LIST.map(item => (
          <Select.Option key={item.value} value={item.value}>
            {item.label}
          </Select.Option>
        ))}
      </Select>
    </>
  );
};

export const UserCustomExpression: React.FC<UserTypeFieldsInter> = props => {
  const { enabled, onChange, value, readonly, hiddenCustomUserFieldSource } = props;
  const { t } = useI18n();
  const fieldAdapter = useCallback(async (values, fetchMethods) => {
    // 在查询的时候将给服务端拼接的多余的标记替换掉
    const originKey = Array.isArray(values) ? values?.map(str => replace(str, /item(.values.|.)/, '')) : values;
    const options = await fetchMethods(originKey, AUTO_USER_FIELD_TYPES);
    return (
      options?.map(item => {
        const fieldTypeKey = get(item, 'fieldType.key', '');
        if (SYSTEM_USER.includes(fieldTypeKey)) {
          item.value = `item.${item.value}`;
        } else {
          item.value = `item.values.${item.value}`;
        }
        return item;
      }) || []
    );
  }, []);

  return (
    <UserField
      name={t('pages.automation.fields.userField')}
      enabled={enabled}
      onChange={onChange}
      value={value}
      type="field"
      fetchOptions={keyword => fieldAdapter(keyword, getUserCustomFieldOptionsByName)}
      fetchValues={values => fieldAdapter(values, getUserCustomFieldOptionsByValues)}
      readonly={readonly}
    >
      {!hiddenCustomUserFieldSource && <SourceSelect />}
    </UserField>
  );
};

const ApprovalUser: React.FC<UserTypeFieldsInter> = ({ type, onChange, value, name, readonly }) => {
  const { t } = useI18n();
  const onCheck = useCallback(
    e => {
      const checked = e?.target?.checked;
      onChange?.(checked, type);
    },
    [onChange, type],
  );

  const onFieldChange = value => {
    onChange?.(value, type, 'approver');
  };

  const onSourceChange = value => {
    onChange?.({ type: value, fieldKey: 'item.objectId' }, type, 'source');
  };

  const approverOptions = useMemo(
    () => [
      { value: 'allUsers', label: t('pages.automation.utils.workflow.allUsers') },
      { value: 'unApprovedUsers', label: t('pages.automation.utils.workflow.unApprovedUsers') },
    ],
    [t],
  );

  return (
    <div className="user-fields">
      <Checkbox
        className={cx('user-field-check')}
        onChange={onCheck}
        checked={value[type]?.enabled}
        disabled={readonly}
      >
        {name}
      </Checkbox>
      <Select
        className={cx('user-field-select')}
        disabled={!value[type]?.enabled || readonly}
        placeholder={t('pages.automation.utils.workflow.userFieldPlaceholder')}
        value={value[type]?.approver}
        onChange={onFieldChange}
        options={approverOptions}
      />
      <SourceSelect fieldType="workflowApprover" value={value} onChange={onSourceChange} readonly={readonly} />
    </div>
  );
};

const CommentWorkspaceRole: React.FC<UserTypeFieldsInter> = ({ type, onChange, value, name, readonly }) => {
  const { t } = useI18n();
  const checked = useMemo(() => !!value[type]?.enabled, [type, value]);
  const onCheck = useCallback(
    e => {
      const checked = e?.target?.checked;
      onChange?.(checked, type);
    },
    [onChange, type],
  );

  const onFieldChange = value => {
    onChange?.(value, type);
  };

  return (
    <div className="user-fields">
      <Checkbox className={cx('user-field-check')} onChange={onCheck} checked={checked} disabled={readonly}>
        {name}
      </Checkbox>
      <DebounceSelect
        className={cx('user-field-input')}
        value={value[type]?.value}
        disabled={!value[type]?.enabled || readonly}
        mode="multiple"
        allowClear
        showSearch
        fetchOptions={getWorkspaceRoleOptionsByName}
        fetchValues={getWorkspaceRoleOptionsByValues}
        placeholder={t('pages.workflow.placeholder.workspaceRole')}
        onChange={onFieldChange}
      />
    </div>
  );
};

const CommentUser: React.FC<UserTypeFieldsInter> = ({ type, onChange, value, name, readonly }) => {
  const { t } = useI18n();
  const [checked, onChecked] = useState(!!value[type]?.enabled);
  const onCheck = useCallback(
    e => {
      const checked = e?.target?.checked;
      onChecked(checked);
      onChange?.(checked, type);
    },
    [onChange, type],
  );

  const onFieldChange = value => {
    onChange?.(value, type);
  };

  const notifierOptions = useMemo(
    () => [
      { value: 'createdBy', label: t('pages.automation.utils.comment.createdBy') },
      { value: 'notifier', label: t('pages.automation.utils.comment.notifier') },
    ],
    [t],
  );

  return (
    <div className="user-fields">
      <Checkbox className={cx('user-field-check')} onChange={onCheck} checked={checked} disabled={readonly}>
        {name}
      </Checkbox>
      <Select
        mode="multiple"
        className={cx('user-field-select')}
        disabled={!checked || readonly}
        placeholder={t('pages.automation.utils.comment.placeholder')}
        value={value[type]?.value}
        onChange={onFieldChange}
        options={notifierOptions}
      />
    </div>
  );
};

/**
 * @desc 用户、用户组、用户类型字段表单组件
 * @param props
 * @constructor
 */
const UserTypeFields: React.FC<UserTypeFieldsInter> = props => {
  const {
    onChange,
    value = {},
    readonly,
    hiddenCustomUserField,
    hiddenCustomUserFieldSource,
    hiddenWorkflowField,
    hiddenCommentField,
    hiddenWorkspaceRoleField,
  } = props;
  const [groupList, setGroupList] = useState([]);
  const { t } = useI18n();

  const onFieldChange = useCallback(
    (chosen, type, source) => {
      let newValue;
      if (typeof chosen === 'boolean') {
        newValue = { ...value, [type]: { ...value[type], enabled: chosen } };
      } else if (type === 'fieldType') {
        newValue = { ...value, field: { ...value?.field, source: { type: chosen } } };
      } else if (type === 'workflowApprover') {
        if (source) {
          newValue = { ...value, [type]: { ...value[type], [source]: chosen } };
        } else {
          newValue = { ...value, [type]: { ...value[type], approver: chosen } };
        }
        newValue[type].type = 'copy';
        newValue[type].fieldTypeKey = 'Text';
      } else {
        newValue = { ...value, [type]: { ...value[type], value: chosen, type: type === 'field' ? 'copy' : 'value' } };
      }
      onChange(newValue);
    },
    [onChange, value],
  );

  useEffect(() => {
    async function fetchGroups() {
      const treeList = await getGroupsTree();
      setGroupList(treeList);
    }
    fetchGroups();
  }, []);

  const fetchOptions = useCallback(async values => {
    return await getUserOptionsByName(values, null, true);
  }, []);

  const fetchValues = useCallback(async values => {
    return await getUserOptionsByValues(values, null, true);
  }, []);

  return (
    <div className={cx('user-container')}>
      <UserField
        name={t('global.user')}
        type="user"
        onChange={onFieldChange}
        value={value}
        fetchOptions={fetchOptions}
        fetchValues={fetchValues}
        readonly={readonly}
      />
      <UserField
        name={t('global.userGroup')}
        onChange={onFieldChange}
        value={value}
        groupList={groupList}
        type="group"
        readonly={readonly}
      />
      {!hiddenCustomUserField && (
        <UserCustomExpression
          hiddenCustomUserFieldSource={hiddenCustomUserFieldSource}
          value={value}
          onChange={onFieldChange}
          readonly={readonly}
        />
      )}
      {!hiddenWorkflowField && (
        <ApprovalUser
          name={t('pages.automation.utils.workflow.workflowApprovers')}
          type="workflowApprover"
          value={value}
          onChange={onFieldChange}
          readonly={readonly}
        />
      )}
      {!hiddenWorkspaceRoleField && (
        <CommentWorkspaceRole
          name={t('pages.workflow.FormField.workspaceRoles')}
          type="workspaceRole"
          value={value}
          onChange={onFieldChange}
          readonly={readonly}
        />
      )}
      {!hiddenCommentField && (
        <CommentUser
          name={t('pages.automation.utils.comment.roles')}
          type="comment"
          value={value}
          onChange={onFieldChange}
          readonly={readonly}
        />
      )}
    </div>
  );
};

export default UserTypeFields;
